import React, { useEffect, useState } from "react";
import axios from "axios";
import "./EditProfile.css";
import { useLocation, useNavigate } from "react-router-dom";
import NavUser from "../components/NavUser";
import { 
  FaUser, 
  FaPhone, 
  FaGlobe, 
  FaFlag, 
  FaHotel, 
  FaUserFriends, 
  FaMapMarkerAlt, 
  FaFileAlt, 
  FaLanguage, 
  FaLock, 
  FaUpload, 
  FaCamera 
} from "react-icons/fa";
import { getFileUrl } from "../utils/imageUrl";

function EditProfile() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_API_URL;


  const userData = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("jwt_token");
  // Agent editing a guide or hotel owner
  const agentUserId =
    location.state?.guide?.id || location.state?.hotelOwner?.id || null;


  const [form, setForm] = useState({
    full_name: "",
    phone_number: "",
    country_id: "",
    nationality_id: "",
    old_password: "",
    password: "",
    repeat_password: "",
    zip_code: "",
    address: "",
    city: "",
    description: "",
    language: "",
    hotel_name: "",
    hotel_zip: "",
    hotel_address: "",
    hotel_city: "",
    property_type_id: "",
    dob: "",
  });

  const [roleId, setRoleId] = useState(null);
  const [countries, setCountries] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [preview, setPreview] = useState(null);

  /* ================= LOAD INITIAL DATA ================= */
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      try {
        //  Fetch reference data
        const [countriesRes, nationalitiesRes, propertyTypesRes] =
          await Promise.all([
            axios.get(`${BACKEND_URL}/countries`),
            axios.get(`${BACKEND_URL}/nationalities`),
            axios.get(`${BACKEND_URL}/property_types`),
          ]);

        setCountries(countriesRes.data);
        setNationalities(nationalitiesRes.data);
        setPropertyTypes(propertyTypesRes.data);

        //  Fetch user data
        let user = null;
        let detectedRoleId = null;

        if (agentUserId) {
          // Agent editing a guide or hotel owner
          const res = await axios.get(
            `${BACKEND_URL}/users/${agentUserId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          user = res.data;
        } else if (userData) {
          // Own profile
          const res = await axios.get(
            `${BACKEND_URL}/users/${userData.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          user = res.data;
        }

        //  Map roles (Handle variations in API naming)
        const guideDataSource = user.Guide || user.guide || {};
        const hotelDataSource = user.Hotel || user.hotel || {};

        if (user.role_id === 2 || user.role_id === 4) {
          detectedRoleId = user.role_id;
          user.GuideDraft = {
            zip_code: guideDataSource.zip_code || "",
            address: guideDataSource.address || "",
            city: guideDataSource.city || "",
            description: guideDataSource.description || "",
            language: guideDataSource.language || "",
          };
          user.HotelDraft = null;
        }

        if (user.role_id === 1 || user.role_id === 3) {
          detectedRoleId = user.role_id;
          user.HotelDraft = {
            name: hotelDataSource.name || "",
            zip_code: hotelDataSource.zip_code || "",
            address: hotelDataSource.address || "",
            city: hotelDataSource.city || "",
            property_type_id: hotelDataSource.property_type_id || "",
          };
          user.GuideDraft = null;
        }

        setRoleId(detectedRoleId);

        //  Prefill form
        setForm({
          full_name: user.full_name || "",
          phone_number: user.phone_number || "",
          country_id: user.country_id || user.Country?.id || user.country?.id || "",
          nationality_id: user.nationality_id || user.Nationality?.id || user.nationality?.id || "",
          zip_code: user.GuideDraft?.zip_code || "",
          address: user.GuideDraft?.address || "",
          city: user.GuideDraft?.city || "",
          description: user.GuideDraft?.description || "",
          language: user.GuideDraft?.language || "",
          hotel_name: user.HotelDraft?.name || "",
          hotel_zip: user.HotelDraft?.zip_code || "",
          hotel_address: user.HotelDraft?.address || "",
          hotel_city: user.HotelDraft?.city || "",
          property_type_id: user.HotelDraft?.property_type_id || "",
          dob: user.dob ? user.dob.split("T")[0] : "",
        });

        setPreview(user.logo ? getFileUrl(user.logo, BACKEND_URL) : null);
      } catch (err) {
        console.error(err);
        alert("Failed to load profile data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [BACKEND_URL, agentUserId, token, userData?.id]);

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfileImage(file);
    setPreview(URL.createObjectURL(file));
  };

  /* ================= UPDATE PROFILE ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = new FormData();
    if (form.full_name) payload.append("full_name", form.full_name);
    if (form.phone_number) payload.append("phone_number", form.phone_number);
    if (form.country_id) payload.append("country_id", form.country_id);
    if (form.nationality_id) payload.append("nationality_id", form.nationality_id);
    if (form.dob) payload.append("dob", form.dob);

    // Passwords
    if (form.old_password) payload.append("old_password", form.old_password);
    if (form.password) {
       if (form.password !== form.repeat_password) {
         alert("Passwords do not match");
         setSaving(false);
         return;
       }
       payload.append("password", form.password);
    }

    if (roleId === 4 || roleId === 2) {
      if (form.zip_code) payload.append("zip_code", form.zip_code);
      if (form.city) payload.append("city", form.city);
      if (form.address) payload.append("address", form.address);
      if (form.description) payload.append("description", form.description);
      if (form.language) payload.append("language", form.language);
    }

    if (roleId === 1 || roleId === 3) {
      if (form.hotel_name) payload.append("name", form.hotel_name);
      if (form.hotel_zip) payload.append("zip_code", form.hotel_zip);
      if (form.hotel_city) payload.append("city", form.hotel_city);
      if (form.hotel_address) payload.append("address", form.hotel_address);
      if (form.property_type_id) payload.append("property_type_id", form.property_type_id);
    }

    if (profileImage) payload.append("logo", profileImage);

    try {
      const userIdToUpdate = location.state?.hotelOwner?.id || location.state?.guide?.id || userData.id;
      const res = await axios.put(
        `${BACKEND_URL}/users/${userIdToUpdate}`,
        payload,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );

      // Refresh localStorage user if editing own profile
      if (userData && (!agentUserId || String(agentUserId) === String(userData.id))) {
        try {
          const freshRes = await axios.get(`${BACKEND_URL}/users/${userData.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (freshRes.data) {
            localStorage.setItem("user", JSON.stringify(freshRes.data));
          }
        } catch (refreshErr) {
          if (res.data?.user) {
            localStorage.setItem("user", JSON.stringify(res.data.user));
          }
        }
      }

      alert("Profile updated successfully");
      navigate(-1); // Go back to profile
    } catch (err) {
      alert(err.response?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-state-premium">Loading...</div>;

  return (
    <div className="traveler-dashboard-premium">
      <NavUser />
      
      {/* 🚀 MINI HERO */}
      <div className="profile-hero mini">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-name">Edit Profile</h1>
            <p className="hero-meta">Customize your public presence and account settings.</p>
          </div>
        </div>
      </div>

      <div className="container-premium edit-mode">
        <form onSubmit={handleSubmit} className="premium-form-layout">
          
          <div className="form-grid-premium">
            
            {/* 📸 IMAGE UPLOAD CARD */}
            <div className="glass-card avatar-edit-card">
              <h3 className="premium-title"><FaCamera /> Profile Photo</h3>
              <div className="avatar-edit-wrapper">
                <div className="avatar-preview-large">
                  <img src={preview || "/default-avatar.png"} alt="Preview" />
                  <label htmlFor="profile_upload" className="upload-overlay">
                    <FaUpload />
                    <input id="profile_upload" type="file" accept="image/*" onChange={handleImageChange} />
                  </label>
                </div>
                <p className="upload-hint">Upload a high-quality JPEG or PNG for your profile.</p>
              </div>
            </div>

            {/* 👤 BASIC INFO CARD */}
            <div className="glass-card">
              <h3 className="premium-title"><FaUser /> Basic Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="full_name">Full Name</label>
                  <div className="input-with-icon">
                    <FaUser /><input id="full_name" name="full_name" value={form.full_name} onChange={handleChange} placeholder="John Doe" autoComplete="name" />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="phone_number">Phone Number</label>
                  <div className="input-with-icon">
                    <FaPhone /><input id="phone_number" name="phone_number" value={form.phone_number} onChange={handleChange} placeholder="+1 234 567 890" autoComplete="tel" />
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="country_id">Country</label>
                  <div className="input-with-icon">
                    <FaGlobe />
                    <select id="country_id" name="country_id" value={form.country_id} onChange={handleChange}>
                      <option value="">Select Country</option>
                      {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="nationality_id">Nationality</label>
                  <div className="input-with-icon">
                    <FaFlag />
                    <select id="nationality_id" name="nationality_id" value={form.nationality_id} onChange={handleChange}>
                      <option value="">Select Nationality</option>
                      {nationalities.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dob">Date of Birth</label>
                  <div className="input-with-icon">
                    <FaFileAlt /><input id="dob" type="date" name="dob" value={form.dob} onChange={handleChange} />
                  </div>
                </div>
              </div>
            </div>

            {/* 🧭 GUIDE DETAILS */}
            {(roleId === 4 || roleId === 2) && (
              <div className="glass-card">
                <h3 className="premium-title"><FaUserFriends /> Guide Portfolio</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="guide_city">City</label>
                    <div className="input-with-icon"><FaMapMarkerAlt /><input id="guide_city" name="city" value={form.city} onChange={handleChange} placeholder="City Name" autoComplete="address-level2" /></div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="guide_language">Languages</label>
                    <div className="input-with-icon"><FaLanguage /><input id="guide_language" name="language" value={form.language} onChange={handleChange} placeholder="English, French..." autoComplete="language" /></div>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="guide_description">Service Description</label>
                  <textarea id="guide_description" name="description" value={form.description} onChange={handleChange} placeholder="Tell travelers about your expertise..." rows="4" />
                </div>
              </div>
            )}

            {/* 🏨 HOTEL DETAILS */}
            {(roleId === 1 || roleId === 3) && (
              <div className="glass-card">
                <h3 className="premium-title"><FaHotel /> Hotel Management</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="hotel_name">Hotel Name</label>
                    <div className="input-with-icon"><FaHotel /><input id="hotel_name" name="hotel_name" value={form.hotel_name} onChange={handleChange} placeholder="Grand Soloway Resort" autoComplete="organization" /></div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="hotel_type">Property Type</label>
                    <div className="input-with-icon">
                      <FaFlag />
                      <select id="hotel_type" name="property_type_id" value={form.property_type_id} onChange={handleChange}>
                        <option value="">Choose Type</option>
                        {propertyTypes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="form-row">
                   <div className="form-group">
                     <label htmlFor="hotel_city">City</label>
                     <div className="input-with-icon"><FaMapMarkerAlt /><input id="hotel_city" name="hotel_city" value={form.hotel_city} onChange={handleChange} placeholder="City" autoComplete="address-level2" /></div>
                   </div>
                   <div className="form-group">
                     <label htmlFor="hotel_address">Address</label>
                     <div className="input-with-icon"><FaMapMarkerAlt /><input id="hotel_address" name="hotel_address" value={form.hotel_address} onChange={handleChange} placeholder="123 Street Ave." autoComplete="street-address" /></div>
                   </div>
                </div>
              </div>
            )}

            {/* 🔐 SECURITY */}
            <div className="glass-card">
              <h3 className="premium-title"><FaLock /> Account Security</h3>
              <div className="form-group">
                <label htmlFor="old_password">Current Password</label>
                <div className="input-with-icon"><FaLock /><input id="old_password" type="password" name="old_password" onChange={handleChange} placeholder="Leave blank to keep current" autoComplete="current-password" /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="new_password">New Password</label>
                  <div className="input-with-icon"><FaLock /><input id="new_password" type="password" name="password" onChange={handleChange} placeholder="••••••••" autoComplete="new-password" /></div>
                </div>
                <div className="form-group">
                  <label htmlFor="repeat_password">Repeat Password</label>
                  <div className="input-with-icon"><FaLock /><input id="repeat_password" type="password" name="repeat_password" onChange={handleChange} placeholder="••••••••" autoComplete="new-password" /></div>
                </div>
              </div>
            </div>

          </div>

          <div className="form-actions-premium">
            <button type="submit" disabled={saving} className="full-btn primary">
              {saving ? "Processing..." : "Sync Profile Changes"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}

export default EditProfile;
